import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../api/api';
import { deriveDefaultsFromGlobals } from '../../utils/defaults';
import { mapTemplateToConfigurator as mapTemplateToConfiguratorBase } from '../../utils/templateMapping';
import {
  getEventScopeStorageKey,
  readSelectedEventScope,
  writeSelectedEventScope,
} from '../../utils/eventScopeStorage';
import TemplateMoveDialog from './TemplateMoveDialog';

const getAssignedGroupIds = userObj => {
  const ids = new Set();
  const primary = Number(userObj?.group?.id || userObj?.groupId || userObj?.group_id);
  if (Number.isFinite(primary) && primary > 0) ids.add(primary);
  if (Array.isArray(userObj?.groups)) {
    userObj.groups.forEach(group => {
      const parsed = Number(group?.id || group?.groupId || group?.group_id);
      if (Number.isFinite(parsed) && parsed > 0) ids.add(parsed);
    });
  }
  return Array.from(ids);
};

const getAvailableGroupsForUser = (userObj, allGroups) => {
  if (userObj?.role?.name === 'Admin') return allGroups || [];
  const assignedIds = new Set(getAssignedGroupIds(userObj));
  if (assignedIds.size === 0) return [];
  return (allGroups || []).filter(group => assignedIds.has(Number(group?.id)));
};

const HIDDEN_EVENTS_KEY = 'hidden_events_by_user';

const readHiddenEvents = userId => {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(HIDDEN_EVENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const list = parsed && typeof parsed === 'object' ? parsed[String(userId)] : [];
    return Array.isArray(list)
      ? list.map(id => Number(id)).filter(Number.isFinite)
      : [];
  } catch {
    return [];
  }
};

const writeHiddenEvents = (userId, ids) => {
  if (!userId) return;
  try {
    const raw = localStorage.getItem(HIDDEN_EVENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[String(userId)] = ids;
    localStorage.setItem(HIDDEN_EVENTS_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
};

const TemplateSelectSection = ({
  onNavigate,
  showNotification,
  onUpdate,
  settings,
  refreshSignal = 0,
  onEventsLoaded,
}) => {
  const { user, activeGroupId } = useAuth();
  const eventScopeStorageKey = useMemo(
    () => getEventScopeStorageKey(user?.id),
    [user?.id]
  );
  const [templates, setTemplates] = useState({ global: [], user: [] });
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLibraryGroup, setActiveLibraryGroup] = useState('all');
  const [copyDialogTemplate, setCopyDialogTemplate] = useState(null);
  const [baseDesignSavingId, setBaseDesignSavingId] = useState(null);
  const [showArchivedEvents, setShowArchivedEvents] = useState(false);
  const [showHiddenEvents, setShowHiddenEvents] = useState(false);
  const [hiddenEventIds, setHiddenEventIds] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const latestFetchRequestRef = useRef(0);
  const lastTemplateEventSyncRef = useRef(null);
  // Removed unused dropdown/status state and key helpers

  // Removed unused click-outside dropdown handling

  useEffect(() => {
    if (!user) return;
    const isAdmin = user?.role?.name === 'Admin';
    // Restore scope from session
    const saved = readSelectedEventScope(user?.id, 'all');
    if (saved) setSelectedEvent(saved);
    const load = async () => {
      try {
        const evts = await api.getEvents();
        setEvents(evts);
        onEventsLoaded?.(evts);
        // If current selection is archived (or no longer visible), reset to 'all'
        if (!isAdmin) {
          const sel = readSelectedEventScope(user?.id, 'all');
          if (sel && evts.every(e => String(e.id) !== String(sel))) {
            setSelectedEvent('all');
            writeSelectedEventScope(user?.id, 'all');
          }
        }
      } catch (e) {
        setEvents([]);
      } finally {
        setEventsLoaded(true);
      }
    };
    load();
  }, [user, onEventsLoaded, eventScopeStorageKey, activeGroupId]);

  useEffect(() => {
    if (!user?.id) return;
    setHiddenEventIds(readHiddenEvents(user.id));
  }, [user?.id]);

  const fetchTemplatesForScope = useCallback(
    async (scope = selectedEvent) => {
      if (!user) return;
      const requestId = ++latestFetchRequestRef.current;
      setLoading(true);
      try {
        const eventId = scope === 'all' ? null : scope;
        const data = await api.getTemplatesByEvent(eventId);
        if (requestId !== latestFetchRequestRef.current) return;
        setTemplates(data);
      } catch (error) {
        if (requestId !== latestFetchRequestRef.current) return;
        console.error('Error fetching templates:', error);
        setTemplates({ global: [], user: [] });
      } finally {
        if (requestId !== latestFetchRequestRef.current) return;
        setLoading(false);
      }
    },
    [selectedEvent, user, activeGroupId]
  );

  useEffect(() => {
    if (!user) return;
    fetchTemplatesForScope();
  }, [user, selectedEvent, fetchTemplatesForScope, refreshSignal, activeGroupId]);

  useEffect(() => {
  const fetchGroups = async () => {
      try {
        const groupsData = await api.getGroups();
        setGroups(groupsData);
      } catch (error) {
        console.error('Failed to fetch groups:', error);
      }
    };
    fetchGroups();
  }, [user]);

  const handleCreateEvent = useCallback(async () => {
    const name = window.prompt('Event name');
    if (!name) return;
    try {
      const payload = { name };
      const isAdminUser = user?.role?.name === 'Admin';
      const candidateGroups = getAvailableGroupsForUser(user, groups);
      if (!isAdminUser && candidateGroups.length > 0) {
        if (candidateGroups.length === 1) {
          payload.visibility = 'group';
          payload.group_ids = [Number(candidateGroups[0].id)];
        } else {
          const groupList = candidateGroups
            .map(group => `${group.id}: ${group.name}`)
            .join('\n');
          const defaultGroupId =
            activeGroupIdNumber && candidateGroups.some(g => Number(g.id) === activeGroupIdNumber)
              ? activeGroupIdNumber
              : candidateGroups[0]?.id;
          const chosen = window.prompt(
            `Choose group permission(s) for this event (comma-separated IDs):\n${groupList}`,
            String(defaultGroupId || '')
          );
          if (!chosen) return;
          const ids = Array.from(
            new Set(
              String(chosen)
                .split(',')
                .map(value => Number(value.trim()))
                .filter(value => Number.isInteger(value) && value > 0)
            )
          );
          const allowed = new Set(
            candidateGroups.map(group => Number(group.id)).filter(Number.isInteger)
          );
          if (!ids.length || ids.some(id => !allowed.has(id))) {
            showNotification('Please select valid group ID(s) from the list.', 'error');
            return;
          }
          payload.visibility = 'group';
          payload.group_ids = ids;
        }
      }

      const result = await api.createEvent(payload);
      if (!result.success) {
        showNotification(result.message || 'Failed to create event', 'error');
        return;
      }
      showNotification('Event created', 'success');
      const evts = await api.getEvents();
      setEvents(evts);
      onEventsLoaded?.(evts);
      const newId = String(result.event.id);
      setSelectedEvent(newId);
      writeSelectedEventScope(user?.id, newId);
    } catch (error) {
      console.error('Failed to create event', error);
      showNotification('Failed to create event', 'error');
    }
  }, [onEventsLoaded, showNotification, user, groups]);

  const handleRenameSelectedEvent = useCallback(async () => {
    if (!selectedEvent || selectedEvent === 'all') {
      showNotification('Select an event first.', 'error');
      return;
    }
    const current = events.find(evt => String(evt.id) === String(selectedEvent));
    const nextName = window.prompt('Rename event', current?.name || '');
    if (!nextName || !nextName.trim() || nextName.trim() === current?.name) return;
    try {
      const result = await api.updateEvent(selectedEvent, { name: nextName.trim() });
      if (!result.success) {
        showNotification(result.message || 'Failed to rename event', 'error');
        return;
      }
      const evts = await api.getEvents();
      setEvents(evts);
      onEventsLoaded?.(evts);
      showNotification('Event renamed.', 'success');
    } catch (error) {
      showNotification(error?.message || 'Failed to rename event', 'error');
    }
  }, [events, onEventsLoaded, selectedEvent, showNotification]);

  const handleDeleteSelectedEvent = useCallback(async () => {
    if (!selectedEvent || selectedEvent === 'all') {
      showNotification('Select an event first.', 'error');
      return;
    }
    const current = events.find(evt => String(evt.id) === String(selectedEvent));
    const confirmed = window.confirm(
      `Delete event "${current?.name || selectedEvent}"? This only works when the event has no templates.`
    );
    if (!confirmed) return;
    try {
      const result = await api.deleteEvent(selectedEvent);
      if (!result.success) {
        showNotification(result.message || 'Failed to delete event', 'error');
        return;
      }
      const evts = await api.getEvents();
      setEvents(evts);
      onEventsLoaded?.(evts);
      setSelectedEvent('all');
      writeSelectedEventScope(user?.id, 'all');
      showNotification('Event deleted.', 'success');
      await fetchTemplatesForScope('all');
    } catch (error) {
      showNotification(error?.message || 'Failed to delete event', 'error');
    }
  }, [
    events,
    fetchTemplatesForScope,
    onEventsLoaded,
    selectedEvent,
    showNotification,
    user?.id,
  ]);

  const handleToggleHideEvent = useCallback(() => {
    if (!selectedEvent || selectedEvent === 'all') {
      showNotification('Select an event first.', 'error');
      return;
    }
    const eventId = Number(selectedEvent);
    if (!Number.isFinite(eventId)) return;
    setHiddenEventIds(prev => {
      const isHidden = prev.some(id => Number(id) === eventId);
      const next = isHidden
        ? prev.filter(id => Number(id) !== eventId)
        : [...prev, eventId];
      writeHiddenEvents(user?.id, next);
      showNotification(
        isHidden ? 'Event unhidden.' : 'Event hidden from your view.',
        'success'
      );
      return next;
    });
  }, [selectedEvent, showNotification, user?.id]);

  useEffect(() => {
    if (settings?.id && settings.id !== 'none') {
      setSelectedTemplate(String(settings.id));
    } else {
      setSelectedTemplate('none');
    }
  }, [settings?.id]);

  // Remove debug logging to reduce console noise

  const mapTemplateToConfigurator = template => {
    if (!template) return {};
    const mappedSettings = mapTemplateToConfiguratorBase(template);
    const parsedData =
      mappedSettings?.data && typeof mappedSettings.data === 'object'
        ? mappedSettings.data
        : null;
    const regmailBlock =
      parsedData && typeof parsedData.regmail === 'object'
        ? parsedData.regmail
        : mappedSettings.regmail || null;

    const regmailKeys = [
      'eventName',
      'hostingOrganisation',
      'eventLocation',
      'eventStartDate',
      'eventEndDate',
      'eventTime',
      'paymentDueDate',
      'eventEmail',
    ];
    const hasRegmailFields = regmailKeys.some(key =>
      Object.prototype.hasOwnProperty.call(mappedSettings, key)
    );
    const categoryName =
      template?.category && typeof template.category === 'string'
        ? template.category.toLowerCase()
        : '';
    const templateName =
      template?.name && typeof template.name === 'string'
        ? template.name.toLowerCase()
        : '';

    const explicitRegmailFlag = (() => {
      if (
        parsedData &&
        typeof parsedData === 'object' &&
        Object.prototype.hasOwnProperty.call(parsedData, 'regmailEnabled')
      ) {
        return Boolean(parsedData.regmailEnabled);
      }
      if (
        Object.prototype.hasOwnProperty.call(template || {}, 'regmailEnabled')
      ) {
        return Boolean(template.regmailEnabled);
      }
      return undefined;
    })();

    const heuristicsSuggestRegmail =
      hasRegmailFields ||
      (categoryName && categoryName.includes('regmail')) ||
      (templateName && templateName.includes('regmail')) ||
      (regmailBlock && Object.keys(regmailBlock).length > 0);

    if (explicitRegmailFlag !== undefined) {
      mappedSettings.regmailEnabled = explicitRegmailFlag;
    } else if (heuristicsSuggestRegmail) {
      mappedSettings.regmailEnabled = true;
    }

    return mappedSettings;
  };

  const extractTemplateData = template => {
    if (!template?.data) return null;
    if (typeof template.data === 'string') {
      try {
        return JSON.parse(template.data);
      } catch {
        return null;
      }
    }
    if (typeof template.data === 'object') return template.data;
    return null;
  };

  const isBaseDesignTemplate = template =>
    Boolean(extractTemplateData(template)?.baseDesign);

  const handleSetBaseDesign = async template => {
    if (!template?.id || !template?.event_id) {
      showNotification('Save the template to an event first.', 'error');
      return;
    }
    if (baseDesignSavingId) return;
    setBaseDesignSavingId(template.id);
    try {
      const templateIsCurrentlyBase = isBaseDesignTemplate(template);
      const shouldBeBase = !templateIsCurrentlyBase;
      await api.saveTemplate(user.id, {
        id: template.id,
        data: { baseDesign: shouldBeBase },
      });
      await fetchTemplatesForScope();
      showNotification(
        shouldBeBase ? 'Base design enabled.' : 'Base design unset.',
        'success'
      );
    } catch (error) {
      showNotification(error?.message || 'Failed to update base design', 'error');
    } finally {
      setBaseDesignSavingId(null);
    }
  };

  const applyNoneSelection = async () => {
    try {
      const gs = await api.getGlobalSettings();
      const defaults = deriveDefaultsFromGlobals(gs);
      onUpdate(null, defaults);
    } catch (e) {
      // Fallback to minimal reset if globals fail
      onUpdate(null, { id: 'none', name: 'None', category: '' });
    }
    setSelectedTemplate('none');
    showNotification('Starting from a blank template.', 'success');
  };

  const handleTemplateSelect = async templateId => {
    const normalizedId = templateId === 'none' ? 'none' : String(templateId);
    if (normalizedId === 'none') {
      await applyNoneSelection();
      return;
    }

    if (selectedTemplate === normalizedId) {
      onNavigate('visualEditor');
      return;
    }

    const allTemplates = [...(templates.global || []), ...(templates.user || [])];
    const templateByExactId = allTemplates.find(
      t => String(t?.id) === normalizedId
    );
    const numericId = Number(normalizedId);
    const templateByNumericId = Number.isNaN(numericId)
      ? null
      : allTemplates.find(t => Number(t?.id) === numericId);
    const template = templateByExactId || templateByNumericId;

    if (template) {
      const mappedSettings = mapTemplateToConfigurator(template);
      onUpdate(null, mappedSettings);
      setSelectedTemplate(normalizedId);
      if (template.event_id) {
        const nextScope = String(template.event_id);
        setSelectedEvent(nextScope);
        writeSelectedEventScope(user?.id, nextScope);
      }
      onNavigate('visualEditor');
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (
      window.confirm(
        `Are you sure you want to delete the "${templateName}" template?`
      )
    ) {
      try {
        const result = await api.deleteUserTemplate(user.id, templateId);
        if (result.success) {
          showNotification('Template deleted successfully!', 'success');
          await fetchTemplatesForScope();
          if (selectedTemplate === String(templateId)) {
            await applyNoneSelection();
          }
        } else {
          showNotification(
            result.message || 'Failed to delete template',
            'error'
          );
        }
      } catch (error) {
        showNotification('Error deleting template', 'error');
      }
    }
  };

  const handleRenameTemplate = async template => {
    if (!template?.id) return;
    const nextName = window.prompt('Rename template', template.name || '');
    if (!nextName || !nextName.trim() || nextName.trim() === template.name) return;
    const result = await api.saveTemplate(user?.id, {
      id: template.id,
      name: nextName.trim(),
    });
    if (!result?.success) {
      showNotification(result?.message || 'Failed to rename template', 'error');
      return;
    }
    showNotification('Template renamed.', 'success');
    await fetchTemplatesForScope();
  };

  const handleDuplicateTemplate = async template => {
    if (!template?.id) return;
    const duplicateName = window.prompt(
      'Duplicate template name',
      `${template.name || 'Template'} Copy`
    );
    if (!duplicateName || !duplicateName.trim()) return;
    const result = await api.duplicateTemplate(
      template.id,
      template.event_id || null,
      duplicateName.trim()
    );
    if (!result?.success) {
      showNotification(result?.message || 'Failed to duplicate template', 'error');
      return;
    }
    showNotification('Template duplicated.', 'success');
    await fetchTemplatesForScope();
  };

  const availableGroups = useMemo(
    () => getAvailableGroupsForUser(user, groups),
    [groups, user]
  );
  const availableGroupIdSet = useMemo(
    () => new Set(availableGroups.map(group => Number(group?.id))),
    [availableGroups]
  );
  const activeGroupIdNumber = useMemo(() => {
    const parsed = Number(activeGroupId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [activeGroupId]);

  const userIsAdmin = user?.role?.name === 'Admin';
  const canSwitchLibraryGroup = userIsAdmin || availableGroups.length > 1;

  useEffect(() => {
    if (!canSwitchLibraryGroup) {
      setActiveLibraryGroup('all');
      return;
    }
    if (activeLibraryGroup === 'all') return;
    const nextId = Number(activeLibraryGroup);
    if (!Number.isFinite(nextId)) {
      setActiveLibraryGroup('all');
      return;
    }
    if (!userIsAdmin && !availableGroupIdSet.has(nextId)) {
      setActiveLibraryGroup('all');
    }
  }, [activeLibraryGroup, availableGroupIdSet, canSwitchLibraryGroup, userIsAdmin]);

  useEffect(() => {
    if (!canSwitchLibraryGroup) return;
    if (!activeGroupIdNumber) return;
    if (!availableGroupIdSet.has(activeGroupIdNumber)) return;
    setActiveLibraryGroup(String(activeGroupIdNumber));
  }, [activeGroupIdNumber, availableGroupIdSet, canSwitchLibraryGroup]);

  const canManageTemplate = useCallback(
    template => {
      if (!template) return false;
      if (userIsAdmin) return true;
      if (template.can_manage || template.can_edit || template.can_delete) return true;
      const visibility = template.visibility || '';
      if (!visibility.startsWith('group-')) return false;
      const groupId = Number(visibility.slice(6));
      if (
        activeGroupIdNumber &&
        availableGroupIdSet.has(activeGroupIdNumber)
      ) {
        return groupId === activeGroupIdNumber;
      }
      return availableGroups.some(group => Number(group.id) === groupId);
    },
    [activeGroupIdNumber, availableGroupIdSet, availableGroups, userIsAdmin]
  );

  const hiddenEventIdSet = useMemo(
    () => new Set(hiddenEventIds.map(id => Number(id)).filter(Number.isFinite)),
    [hiddenEventIds]
  );

  const displayEvents = useMemo(() => {
    return (events || []).filter(evt => {
      const isArchived = evt.status === 'archived';
      const isHidden = hiddenEventIdSet.has(Number(evt.id));
      if (!showArchivedEvents && isArchived) return false;
      if (!showHiddenEvents && isHidden) return false;
      return true;
    });
  }, [events, hiddenEventIdSet, showArchivedEvents, showHiddenEvents]);

  const sortedEvents = useMemo(
    () => [...displayEvents].sort((a, b) => a.name.localeCompare(b.name)),
    [displayEvents]
  );

  useEffect(() => {
    if (!eventsLoaded) return;
    if (!selectedEvent || selectedEvent === 'all') return;
    const stillVisible = displayEvents.some(
      evt => String(evt.id) === String(selectedEvent)
    );
    if (!stillVisible) {
      setSelectedEvent('all');
      writeSelectedEventScope(user?.id, 'all');
    }
  }, [displayEvents, selectedEvent, user?.id]);

  useEffect(() => {
    const templateId =
      settings?.id && settings.id !== 'none' && settings.id !== 'new'
        ? String(settings.id)
        : null;
    const eventId = settings?.event_id || settings?.eventId || null;
    const parsed = Number(eventId);
    if (!templateId || !Number.isFinite(parsed) || parsed <= 0) return;
    const lastSync = lastTemplateEventSyncRef.current;
    if (lastSync && lastSync.templateId === templateId) return;
    const nextScope = String(parsed);
    lastTemplateEventSyncRef.current = { templateId, eventId: parsed };
    if (selectedEvent === nextScope) return;
    setSelectedEvent(nextScope);
    writeSelectedEventScope(user?.id, nextScope);
  }, [settings?.id, settings?.event_id, settings?.eventId, selectedEvent, user?.id]);

  const describeVisibility = useCallback(
    visibility => {
      if (!visibility) return 'Draft (not saved)';
      if (visibility === 'global') return 'Global template';
      if (visibility === 'user') return 'My template';
      if (typeof visibility === 'string' && visibility.startsWith('group-')) {
        const id = visibility.replace('group-', '');
        const visibilityGroups = userIsAdmin ? groups : availableGroups;
        const group = visibilityGroups.find(g => String(g.id) === String(id));
        return group ? `Shared with ${group.name}` : 'Shared with a group';
      }
      return visibility;
    },
    [groups, availableGroups, userIsAdmin]
  );

  const sortByName = useCallback(
    list =>
      [...list].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''))
      ),
    []
  );

  const formatUpdatedAt = useCallback(value => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  const filterBySearch = useCallback(
    template => {
      const query = searchTerm.trim().toLowerCase();
      if (!query) return true;
      const fields = [
        template.name,
        template.category,
        template.visibility,
        template.ownerUsername,
        template.owner_username,
      ];
      return fields
        .filter(Boolean)
        .map(value => String(value).toLowerCase())
        .some(value => value.includes(query));
    },
    [searchTerm]
  );

  const templateSections = useMemo(() => {
    const globalTemplates = sortByName(
      (templates.global || []).filter(
        tmpl => !(tmpl.visibility && tmpl.visibility.startsWith('group-'))
      )
    );
    const sharedTemplates = (templates.global || []).filter(
      tmpl =>
        tmpl.visibility &&
        tmpl.visibility.startsWith('group-') &&
        (activeLibraryGroup === 'all' ||
          Number(String(tmpl.visibility).replace('group-', '')) ===
            Number(activeLibraryGroup))
    );
    const userTemplates = templates.user || [];
    const templatesSection = sortByName([...sharedTemplates, ...userTemplates]);

    const sections = [
      { key: 'global', title: 'Global library', templates: globalTemplates },
      { key: 'templates', title: 'Templates', templates: templatesSection },
    ];

    const filteredSections = sections
      .map(section => ({
        ...section,
        templates: section.templates.filter(filterBySearch),
      }))
      .filter(section => section.templates.length > 0);

    if (filteredSections.length > 0) return filteredSections;

    const fallbackTemplates = sortByName(
      [...(templates.global || []), ...(templates.user || [])].filter(
        filterBySearch
      )
    );
    if (fallbackTemplates.length > 0) {
      return [{ key: 'all', title: 'Templates', templates: fallbackTemplates }];
    }

    return [];
  }, [activeLibraryGroup, filterBySearch, sortByName, templates]);

  const currentTemplateMeta = useMemo(() => {
    const sourceTemplate = [...(templates.global || []), ...(templates.user || [])].find(
      tmpl => String(tmpl.id) === selectedTemplate
    );

    const workingName = settings?.name?.trim() || sourceTemplate?.name || 'Untitled design';
    const visibility =
      settings?.visibility || sourceTemplate?.visibility || (settings?.id ? 'user' : null);
    const updatedAt =
      settings?.updatedAt ||
      settings?.updated_at ||
      sourceTemplate?.updatedAt ||
      sourceTemplate?.updated_at ||
      null;

    return {
      id:
        settings?.id && settings.id !== 'none' && settings.id !== 'new'
          ? settings.id
          : sourceTemplate?.id || null,
      name: workingName,
      visibility,
      category: settings?.category || sourceTemplate?.category || null,
      owner:
        settings?.ownerUsername ||
        settings?.owner?.username ||
        sourceTemplate?.ownerUsername ||
        sourceTemplate?.owner?.username ||
        null,
      updatedAt,
      isPersisted:
        Boolean(
          (settings?.id && settings.id !== 'none' && settings.id !== 'new') ||
            (sourceTemplate && sourceTemplate.id)
        ),
    };
  }, [selectedTemplate, settings, templates]);

  const hasSearch = Boolean(searchTerm.trim());

  const TemplateCard = ({ template }) => {
    const templateId = String(template.id);
    const isActive = selectedTemplate === templateId;
    const lastUpdatedLabel = formatUpdatedAt(
      template.updatedAt || template.updated_at || template.updated_at?.date
    );
    const templateOwner = template.ownerUsername || template.owner?.username;
    const baseDesign = isBaseDesignTemplate(template);
    const canManage = canManageTemplate(template);
    const canSetBaseDesign =
      Boolean(template?.event_id) &&
      canManage;

    return (
      <div
        className={`rounded-xl border border-slate-200 bg-white/70 p-4 transition ${
          isActive ? 'ring-2 ring-primary/40 border-transparent shadow-sm' : 'hover:border-primary/40'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-slate-900 leading-5 line-clamp-2">
              {template.name}
            </h3>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
              {templateOwner && <span>{templateOwner}</span>}
              {lastUpdatedLabel && <span>• Updated {lastUpdatedLabel}</span>}
            </div>
            {template.description && (
              <p className="text-xs leading-5 text-slate-500 line-clamp-2">
                {template.description}
              </p>
            )}
          </div>
          {isActive && (
            <span className="rounded-full bg-primary/10 px-2 py-[2px] text-[11px] font-semibold text-primary">
              Active
            </span>
          )}
          {baseDesign && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200/70 bg-emerald-50/80 px-2.5 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Base design
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleTemplateSelect(template.id)}
            className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
              isActive
                ? 'border-primary bg-primary text-white shadow-sm'
                : 'border-slate-300 bg-white text-slate-700 hover:border-primary/50'
            }`}
          >
            {isActive ? 'Open in editor' : 'Use template'}
          </button>
          <button
            type="button"
            onClick={() => setCopyDialogTemplate(template)}
            className="text-xs font-semibold text-slate-500 transition hover:text-primary"
          >
            Copy to event
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => handleRenameTemplate(template)}
              className="text-xs font-semibold text-slate-500 transition hover:text-primary"
            >
              Rename
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => handleDuplicateTemplate(template)}
              className="text-xs font-semibold text-slate-500 transition hover:text-primary"
            >
              Duplicate
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => handleDeleteTemplate(template.id, template.name)}
              className="text-xs font-semibold text-rose-500 transition hover:text-rose-600"
            >
              Delete
            </button>
          )}
          {canSetBaseDesign && (
            <button
              type="button"
              onClick={() => handleSetBaseDesign(template)}
              className="text-xs font-semibold text-emerald-700 transition hover:text-emerald-800"
              disabled={baseDesignSavingId === template.id}
            >
              {baseDesignSavingId === template.id
                ? 'Updating…'
                : baseDesign
                ? 'Unset base design'
                : 'Set base design'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-10 text-slate-800">
      <div className="glass-panel p-5 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">
              Current template
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900 leading-6">
                {currentTemplateMeta.name}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
              <span>{describeVisibility(currentTemplateMeta.visibility)}</span>
              {currentTemplateMeta.category && <span>• {currentTemplateMeta.category}</span>}
              {currentTemplateMeta.updatedAt && (
                <span>• Updated {formatUpdatedAt(currentTemplateMeta.updatedAt)}</span>
              )}
              {!currentTemplateMeta.isPersisted && <span>• Unsaved draft</span>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onNavigate('visualEditor')}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-primary/90"
            >
              Open visual editor
            </button>
            <button
              type="button"
              onClick={() => handleTemplateSelect('none')}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-primary/40"
            >
              Start blank
            </button>
            <button
              type="button"
              onClick={handleCreateEvent}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/40"
            >
              New event
            </button>
            <button
              type="button"
              onClick={handleRenameSelectedEvent}
              disabled={!selectedEvent || selectedEvent === 'all'}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Rename event
            </button>
            <button
              type="button"
              onClick={handleDeleteSelectedEvent}
              disabled={!selectedEvent || selectedEvent === 'all'}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Delete event
            </button>
            <button
              type="button"
              onClick={handleToggleHideEvent}
              disabled={!selectedEvent || selectedEvent === 'all'}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {hiddenEventIdSet.has(Number(selectedEvent))
                ? 'Unhide event'
                : 'Hide event'}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Scope
            </label>
            <select
              value={selectedEvent}
              onChange={e => {
                const v = e.target.value;
                setSelectedEvent(v);
                writeSelectedEventScope(user?.id, v);
              }}
              className="form-select mt-1 w-full text-sm"
              aria-label="Select event scope"
            >
              <option value="all">All events</option>
              {sortedEvents.map(evt => (
                <option key={evt.id} value={String(evt.id)}>
                  {evt.name}
                  {evt.status === 'archived' ? ' (Archived)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label
              className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
              htmlFor="template-search"
            >
              Search
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                id="template-search"
                type="search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Filter by name, visibility, or owner"
                className="form-input flex-1 text-sm"
              />
              {hasSearch && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-xs font-semibold text-slate-500 transition hover:text-primary"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {canSwitchLibraryGroup && (
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Library group
              </label>
              <select
                value={activeLibraryGroup}
                onChange={e => setActiveLibraryGroup(e.target.value)}
                className="form-select mt-1 w-full text-sm"
                aria-label="Filter templates by group"
              >
                <option value="all">All groups</option>
                {availableGroups.map(group => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            <button
              type="button"
              onClick={() => setShowArchivedEvents(prev => !prev)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                showArchivedEvents
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-slate-200 bg-white text-slate-500 hover:text-primary hover:border-primary/40'
              }`}
            >
              <span className="text-[10px] uppercase tracking-[0.2em]">Archived</span>
              <span>{showArchivedEvents ? 'On' : 'Off'}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowHiddenEvents(prev => !prev)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                showHiddenEvents
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-slate-200 bg-white text-slate-500 hover:text-amber-700 hover:border-amber-300'
              }`}
            >
              <span className="text-[10px] uppercase tracking-[0.2em]">Hidden</span>
              <span>{showHiddenEvents ? 'On' : 'Off'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200/70 bg-white/60 h-32 animate-pulse" />
            ))}
          </div>
        ) : templateSections.length > 0 ? (
          templateSections.map(section => (
            <section
              key={section.key}
              className="rounded-2xl border border-slate-200/70 bg-white/80"
            >
              <div className="flex items-center justify-between px-4 py-3">
                <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  {section.title}
                </h3>
                <span className="text-[11px] font-medium text-slate-400">
                  {section.templates.length} template
                  {section.templates.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="grid gap-3 border-t border-slate-200/70 px-4 py-4 sm:grid-cols-2 xl:grid-cols-3">
                {section.templates.map(template => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            </section>
          ))
        ) : (
          <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-8 text-center text-sm text-slate-600">
            {hasSearch
              ? 'No templates match your search. Clear the filter or adjust your query.'
              : 'No templates available yet. Start blank or ask an admin to share templates with you.'}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 md:flex-row md:items-center md:justify-between text-slate-700">
        <div className="flex flex-wrap items-center gap-3" />
        <button onClick={() => onNavigate('appearance')} className="nav-button-primary w-full sm:w-auto">
          Next: General settings
        </button>
      </div>
      {copyDialogTemplate && (
        <TemplateMoveDialog
          open={Boolean(copyDialogTemplate)}
          onClose={() => setCopyDialogTemplate(null)}
          template={copyDialogTemplate}
          mode="copy"
          user={user}
          showNotification={showNotification}
          onMoved={async () => {
            await fetchTemplatesForScope();
          }}
          fallbackEvents={sortedEvents}
        />
      )}
    </div>
  );
};

export default TemplateSelectSection;
